import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { InfoBox, Label, NativeSelect, Spinner } from '@immo/ui-kit';
import type {
  AdminConnectedClientItem,
  AdminApiKeyListItem,
  McpCredentialKind,
  McpCredentialSeat,
  McpToolReachabilityResponse,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { FilterPill } from '@/components/filter-bar';
import { useTranslations } from '@/i18n/use-translations';
import { ALL_WORKSPACES_QUERY } from '@/lib/test-workspaces/include-test-workspaces';

const CREDENTIAL_KINDS: ReadonlyArray<McpCredentialKind> = ['api-key', 'oauth-grant'];

type CredentialOption = {
  readonly id: string;
  readonly label: string;
  /** Empty for an API key, which has exactly one seat by construction. */
  readonly seats: ReadonlyArray<McpCredentialSeat>;
};

/**
 * The credential list for one kind.
 *
 * A failed read is its OWN state, never an empty one (BL-058): an operator who
 * cannot tell "there are no OAuth grants" from "the grant list did not load"
 * will conclude the customer has no client — on the one screen they open when
 * they already believe something is wrong.
 */
type CredentialsLoad =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly options: ReadonlyArray<CredentialOption> }
  | { readonly kind: 'failed' };

/**
 * The verdict. `seat-required` is not an error state — it is the panel asking
 * its second question, and it carries the candidates the refusal handed back so
 * the picker needs no further request.
 */
type Verdict =
  | { readonly kind: 'none' }
  | { readonly kind: 'checking' }
  | { readonly kind: 'answered'; readonly answer: McpToolReachabilityResponse }
  | { readonly kind: 'seat-required'; readonly seats: ReadonlyArray<McpCredentialSeat> }
  | { readonly kind: 'unknown-credential' }
  | { readonly kind: 'failed' };

function seatsOf(error: unknown): ReadonlyArray<McpCredentialSeat> {
  // `details` is typed `Record<string, unknown>` on the wire error, so the shape
  // is narrowed here rather than trusted — a refusal that arrived without its
  // candidates must render as a plain failure, not as an empty picker.
  const seats = (error as { error?: { details?: { seats?: unknown } } } | undefined)?.error?.details?.seats;

  return Array.isArray(seats) ? (seats as ReadonlyArray<McpCredentialSeat>) : [];
}

/**
 * „Warum ist dieses Werkzeug für diesen Zugang nicht erreichbar?" (IMM-625.4).
 *
 * Runs on `backoffice:identity:mcp-surface-read` — diagnosing is not changing —
 * so it deliberately sits OUTSIDE the manage gate that wraps the row's exposure
 * controls.
 *
 * The credential lists are fetched on first open, one kind at a time: the page
 * already makes five requests on load, and a picker nobody opens should not cost
 * two more on every visit.
 */
export function ToolReachabilityPanel({ toolId }: { readonly toolId: string }) {
  const t = useTranslations('pages.admin.mcp-access.tools.reachability');
  const intl = useIntl();

  const [kind, setKind] = useState<McpCredentialKind>('api-key');
  const [credentials, setCredentials] = useState<Record<McpCredentialKind, CredentialsLoad>>({
    'api-key': { kind: 'idle' },
    'oauth-grant': { kind: 'idle' },
  });
  const [credentialId, setCredentialId] = useState('');
  const [seatId, setSeatId] = useState('');
  const [verdict, setVerdict] = useState<Verdict>({ kind: 'none' });
  // Every check is stamped, and only the newest one may write a verdict. Without
  // it, switching credential kind mid-flight lets the first request's answer
  // land on top of the second's — the panel would then state a verdict about a
  // credential the operator is no longer looking at, which is the one thing this
  // screen must never do.
  const [checkToken, setCheckToken] = useState(0);

  const load = credentials[kind];

  // The panel opens on „API-Schlüssel", so its list has to be asked for without
  // a click — a picker that populates only once the operator toggles the kind
  // and toggles back is a control that looks broken on the default choice.
  // Mounted-only: the panel is mounted when its row expands and unmounted when
  // it closes, so this fires exactly once per opening.
  // Empty deps on purpose: `loadCredentials` guards its own re-entry on `idle`,
  // so re-running it would be a no-op rather than a second request.
  useEffect(() => {
    void loadCredentials('api-key');
  }, []);

  async function loadCredentials(next: McpCredentialKind): Promise<void> {
    if (credentials[next].kind !== 'idle') return;

    setCredentials((current) => ({ ...current, [next]: { kind: 'loading' } }));

    try {
      const options = next === 'api-key' ? await loadApiKeys() : await loadGrants();

      setCredentials((current) => ({ ...current, [next]: { kind: 'ready', options } }));
    } catch {
      setCredentials((current) => ({ ...current, [next]: { kind: 'failed' } }));
    }
  }

  async function loadApiKeys(): Promise<ReadonlyArray<CredentialOption>> {
    // `kind: 'workspace'` because an admin API key binds to an operator rather
    // than to a member in a workspace, so none of the three layers apply to one
    // and the endpoint refuses it anyway. Every workspace explicitly: the
    // operator's test-workspace filter narrows lists they are BROWSING, and a
    // support question about a test workspace's key is still a support question.
    const result = await apiClient.adminApiKeys.list({
      query: { kind: 'workspace', ...ALL_WORKSPACES_QUERY },
    });

    if (!result.ok) throw new Error('api keys unavailable');

    return result.body.data.map((key: AdminApiKeyListItem) => ({
      id: key.id,
      label: key.workspaceName === null ? key.name : `${key.name} · ${key.workspaceName}`,
      seats: [],
    }));
  }

  function formatLastUsed(lastUsedAt: string | null): string {
    return lastUsedAt === null ? t('never') : intl.formatDate(lastUsedAt, { dateStyle: 'short', timeStyle: 'short' });
  }

  async function loadGrants(): Promise<ReadonlyArray<CredentialOption>> {
    const result = await apiClient.adminConnectedClients.list({ query: {} });

    if (!result.ok) throw new Error('connected clients unavailable');

    // Client name, bound person and last use, because a customer commonly holds
    // several grants for the same client and the question is always about ONE of
    // them. The list arrives ordered by last use, so the grant a member is asking
    // about is normally the first option. Seats are deliberately NOT in the label
    // — a person with three Standorte would make the option unreadable, and the
    // seat picker below asks for exactly that when it matters. An unscoped grant
    // reads „Alle Werkzeuge"; a scoped grant's permissions are never listed,
    // because the verdict names the missing one.
    return result.body.items.map((grant: AdminConnectedClientItem) => ({
      id: grant.id,
      label: [
        grant.clientName,
        grant.userEmail,
        grant.access === 'ALL_TOOLS' ? t('all-tools') : null,
        formatLastUsed(grant.lastUsedAt),
      ]
        .filter((part) => part !== null)
        .join(' · '),
      seats: grant.seats,
    }));
  }

  function selectKind(next: McpCredentialKind): void {
    setKind(next);
    setCredentialId('');
    setSeatId('');
    setVerdict({ kind: 'none' });
    // Invalidates any check still in flight: its answer is about a credential of
    // the kind the operator has just navigated away from.
    setCheckToken((current) => current + 1);
    void loadCredentials(next);
  }

  async function check(nextCredentialId: string, nextSeatId: string): Promise<void> {
    if (nextCredentialId === '') {
      setVerdict({ kind: 'none' });
      setCheckToken((current) => current + 1);

      return;
    }

    const token = checkToken + 1;

    setCheckToken(token);
    setVerdict({ kind: 'checking' });

    const settle = (next: Verdict): void => {
      // `setCheckToken`'s updater form reads the value at flush time, so a later
      // check that has already bumped it wins and this answer is dropped.
      setCheckToken((current) => {
        if (current === token) setVerdict(next);

        return current;
      });
    };

    try {
      const result = await apiClient.adminMcpSurface.toolReachability({
        pathParams: { toolId },
        query: {
          credentialKind: kind,
          credentialId: nextCredentialId,
          ...(nextSeatId === '' ? {} : { memberId: nextSeatId }),
        },
      });

      if (result.ok) {
        settle({ kind: 'answered', answer: result.body });

        return;
      }

      const code = (result.body as { error?: { code?: string } } | undefined)?.error?.code;

      if (code === 'identity.mcp-surface.seat-required') {
        const seats = seatsOf(result.body);

        // A refusal that arrived without its candidates cannot become a picker,
        // and a state that renders nothing at all is worse than an error — the
        // operator is left with a panel that swallowed their click and says
        // nothing (BL-061). Fall back to the generic failure, which at least
        // tells them to try again.
        settle(seats.length > 1 ? { kind: 'seat-required', seats } : { kind: 'failed' });

        return;
      }

      settle(
        code === 'identity.mcp-surface.credential-not-found' ? { kind: 'unknown-credential' } : { kind: 'failed' },
      );
    } catch {
      settle({ kind: 'failed' });
    }
  }

  function selectCredential(nextId: string): void {
    setCredentialId(nextId);
    setSeatId('');
    void check(nextId, '');
  }

  function selectSeat(nextSeatId: string): void {
    setSeatId(nextSeatId);
    void check(credentialId, nextSeatId);
  }

  // The picker's own seats when the grant is multi-seat, plus whatever a
  // `seat-required` refusal handed back — the refusal is authoritative, because
  // it is the surface's own answer about which seats exist right now.
  const seats =
    verdict.kind === 'seat-required'
      ? verdict.seats
      : ((load.kind === 'ready' ? load.options.find((option) => option.id === credentialId)?.seats : undefined) ?? []);

  return (
    <div className="flex flex-col gap-4 px-4 py-4" role="region" aria-label={t('heading')}>
      <div role="group" aria-label={t('kind-label')} className="flex flex-wrap items-center gap-2">
        {CREDENTIAL_KINDS.map((option) => (
          <FilterPill
            key={option}
            label={t(`kind.${option}`)}
            active={kind === option}
            onToggle={() => selectKind(option)}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <Label htmlFor={`reachability-credential-${toolId}`}>{t('credential-label')}</Label>
          {load.kind === 'failed' ? (
            <InfoBox type="danger">{t('load-error')}</InfoBox>
          ) : load.kind === 'ready' && load.options.length === 0 ? (
            <InfoBox type="info">{t(`empty.${kind}`)}</InfoBox>
          ) : (
            <NativeSelect
              id={`reachability-credential-${toolId}`}
              value={credentialId}
              onChange={selectCredential}
              disabled={load.kind !== 'ready'}
              placeholder={t('credential-placeholder')}
              options={load.kind === 'ready' ? load.options.map(({ id, label }) => ({ value: id, label })) : []}
            />
          )}
        </div>

        {/* Rendered only when the answer actually depends on a seat — the common
            single-seat case costs no extra click and no extra request. */}
        {seats.length > 1 && (
          <div className="flex flex-col gap-1">
            <Label htmlFor={`reachability-seat-${toolId}`}>{t('seat-label')}</Label>
            <NativeSelect
              id={`reachability-seat-${toolId}`}
              value={seatId}
              onChange={selectSeat}
              placeholder={t('seat-placeholder')}
              options={seats.map((seat) => ({ value: seat.memberId, label: seat.workspaceName }))}
            />
          </div>
        )}
      </div>

      {seats.length > 1 && <p className="text-base-sm text-text-secondary">{t('seat-hint')}</p>}

      {verdict.kind === 'checking' && (
        <p className="flex items-center gap-2 text-base-sm text-text-secondary">
          <Spinner size="sm" />
          {t('loading')}
        </p>
      )}
      {verdict.kind === 'unknown-credential' && <InfoBox type="warning">{t('credential-not-found')}</InfoBox>}
      {verdict.kind === 'failed' && <InfoBox type="danger">{t('error')}</InfoBox>}
      {verdict.kind === 'answered' && <Answer answer={verdict.answer} />}
    </div>
  );
}

/**
 * One sentence, naming the layer the client actually hits. The permission is
 * interpolated for the two layers whose copy names it, so support can relay the
 * literal a member has to be granted.
 */
function Answer({ answer }: { readonly answer: McpToolReachabilityResponse }) {
  const t = useTranslations('pages.admin.mcp-access.tools.reachability');

  if (answer.excludedBy === null) return <InfoBox type="success">{t('reachable')}</InfoBox>;

  return (
    <InfoBox type="warning">{t(`excluded.${answer.excludedBy}`, { permission: answer.requiredPermission })}</InfoBox>
  );
}
